/* Shared staff portal pieces: session hook, login screen, shell layout. */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Bell, LogOut, ShieldCheck } from "lucide-react";
import config from "../../config";
import { brandLogo, useSiteContent } from "../../lib/siteContent";
import { fieldError, indianMobile10 } from "../../lib/validation";
import ForgotPassword from "../../components/auth/ForgotPassword";
import OtpActions from "../../components/auth/OtpActions";

export function staffHeaders() {
  const token = localStorage.getItem("customer_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function readStaffUser() {
  try {
    return JSON.parse(localStorage.getItem("customer_user") || "{}");
  } catch {
    return {};
  }
}

export function detailMessage(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (typeof d === "object" && d.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x.msg || x).join(", ");
  return "Something went wrong";
}

export async function openAuthedFile(url, onError, extraHeaders, options) {
  const { allowHtml = false, filenameFallback = "document" } = options || {};
  try {
    const res = await axios.get(url, { headers: extraHeaders || staffHeaders(), responseType: "blob" });
    const type = (res.headers["content-type"] || res.data?.type || "application/octet-stream").split(";")[0];
    if (type.includes("application/json") || (type.includes("text/html") && !allowHtml)) {
      let msg = "Could not open file";
      try {
        const text = await res.data.text();
        const parsed = JSON.parse(text);
        if (typeof parsed?.detail === "string") msg = parsed.detail;
      } catch {
        /* ignore */
      }
      if (onError) onError(msg);
      return;
    }
    const blob = new Blob([res.data], { type: type || "application/octet-stream" });
    if (allowHtml && type.includes("text/html")) {
      const obj = URL.createObjectURL(blob);
      window.open(obj, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(obj), 60000);
      return;
    }
    const cd = res.headers["content-disposition"] || res.headers["Content-Disposition"] || "";
    const starred = cd.match(/filename\*=UTF-8''([^;]+)/i);
    const quoted = cd.match(/filename=\"([^\"]+)\"/i) || cd.match(/filename=([^;]+)/i);
    let filename = filenameFallback;
    if (starred?.[1]) filename = decodeURIComponent(starred[1]);
    else if (quoted?.[1]) filename = quoted[1].trim().replace(/[\"']/g, "");
    else {
      const ext = type.includes("pdf") ? ".pdf" : type.includes("png") ? ".png" : type.includes("jpeg") || type.includes("jpg") ? ".jpg" : "";
      filename = `${filenameFallback}${ext}`;
    }
    const obj = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = obj;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(obj), 2500);
  } catch (err) {
    let msg = "Could not open file";
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        if (typeof parsed?.detail === "string") msg = parsed.detail;
      } catch {
        /* ignore */
      }
    } else if (typeof data?.detail === "string") {
      msg = data.detail;
    }
    if (onError) onError(msg);
  }
}

export function staffLogout() {
  localStorage.removeItem("customer_token");
  localStorage.removeItem("customer_role");
  localStorage.removeItem("customer_user");
  window.dispatchEvent(new Event("customer-auth-changed"));
}

/** Session gate for a staff portal. status: checking | login | ready */
export function useStaffSession(expectedRole, extraRoles = []) {
  const allowed = [expectedRole, "super_admin", "ops_admin", ...extraRoles];
  const [status, setStatus] = useState("checking");
  const [user, setUser] = useState(readStaffUser());

  const check = useCallback(async () => {
    const token = localStorage.getItem("customer_token");
    if (!token) {
      setStatus("login");
      return;
    }
    try {
      const { data } = await axios.get(`${config.API}/customer/me`, { headers: staffHeaders() });
      if (allowed.includes(data.role)) {
        setUser(data);
        localStorage.setItem("customer_user", JSON.stringify(data));
        setStatus("ready");
      } else {
        setStatus("login");
      }
    } catch {
      setStatus("login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedRole]);

  useEffect(() => { check(); }, [check]);

  const onAuthed = (data) => {
    localStorage.setItem("customer_token", data.token);
    localStorage.setItem("customer_role", data.role || expectedRole);
    localStorage.setItem("customer_user", JSON.stringify(data.user || {}));
    window.dispatchEvent(new Event("customer-auth-changed"));
    setUser(data.user || {});
    setStatus("ready");
  };

  const logout = () => {
    staffLogout();
    setUser({});
    setStatus("login");
  };

  return { status, user, onAuthed, logout, refreshUser: check };
}

/** Login screen for one staff role: phone OTP or email + password. */
export function StaffLogin({ role, title, subtitle, onAuthed }) {
  const [method, setMethod] = useState("otp"); // otp | password
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("phone");
  const [hint, setHint] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const err = fieldError({ phone });
      if (err) { setError(err); return; }
      const { data } = await axios.post(`${config.API}/auth/otp/send`, { phone: indianMobile10(phone) });
      setStep("otp");
      setHint(data.message || "OTP sent");
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${config.API}/auth/otp/verify`, { phone: indianMobile10(phone), code, role });
      onAuthed(data);
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const err = fieldError({ email });
      if (err) { setError(err); return; }
      if (!password) { setError("Enter your password"); return; }
      const { data } = await axios.post(`${config.API}/auth/customer/login`, { email, password });
      if (data.role !== role) {
        setError(`This account is not a ${role} account. Use the right portal or phone OTP.`);
        return;
      }
      onAuthed(data);
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const site = useSiteContent();
  const logoSrc = brandLogo(site);

  return (
    <div className="min-h-screen bg-[#fdfcfb] flex items-center justify-center px-4 py-10" data-testid={`${role}-login`}>
      <div className="w-full max-w-md bg-white rounded-3xl border border-[#673c96]/10 p-8 shadow-sm">
        <Link
          to="/"
          className="flex flex-col items-start justify-center shrink-0 mb-6"
          style={{ minWidth: '170px' }}
        >
          <img
            src={logoSrc}
            alt="BookMyJab"
            style={{
              width: '160px',
              minWidth: '140px',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
          <span
            className="text-[9px] md:text-[10px] italic font-medium text-[#311b52]/70 tracking-[0.12em] uppercase leading-tight mt-0.5"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Uncompromising Care. Seamlessly Yours.
          </span>
        </Link>
        <div className="mt-4 flex items-center gap-2">
          <span className="w-10 h-10 rounded-2xl bg-[#673c96]/10 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#673c96]" />
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#f37721]">Staff portal</span>
        </div>
        <h1 className="mt-3 text-3xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>{title}</h1>
        <p className="mt-2 text-sm text-[#673c96]" style={{ fontFamily: "Montserrat" }}>{subtitle}</p>

        <div className="mt-5 flex rounded-full border border-[#673c96]/15 p-1">
          <button type="button" onClick={() => { setMethod("otp"); setError(""); }}
            className={`flex-1 rounded-full py-2 text-sm font-semibold ${method === "otp" ? "bg-[#311b52] text-white" : "text-[#673c96]"}`}
            data-testid="staff-method-otp">
            Phone OTP
          </button>
          <button type="button" onClick={() => { setMethod("password"); setError(""); setStep("phone"); setCode(""); }}
            className={`flex-1 rounded-full py-2 text-sm font-semibold ${method === "password" ? "bg-[#311b52] text-white" : "text-[#673c96]"}`}
            data-testid="staff-method-password">
            Email & password
          </button>
        </div>

        {method === "otp" && step === "phone" && (
          <form onSubmit={sendOtp} className="mt-6 space-y-4">
            <input required value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              maxLength={10} placeholder="Registered 10-digit mobile"
              className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="staff-otp-phone" />
            <button disabled={loading} className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold" data-testid="staff-otp-send">
              {loading ? "Sending…" : "Send OTP"}
            </button>
            <p className="text-xs text-[#6b7280]">Only phones registered by the BookMyJab admin team can sign in here.</p>
          </form>
        )}

        {method === "otp" && step === "otp" && (
          <form onSubmit={verify} className="mt-6 space-y-4">
            <p className="text-sm text-[#673c96]">{hint}</p>
            <input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit OTP"
              className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3 tracking-widest text-center" data-testid="staff-otp-code" />
            <button disabled={loading} className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold" data-testid="staff-otp-verify">
              {loading ? "Verifying…" : "Verify & sign in"}
            </button>
            <OtpActions
              loading={loading}
              onChangeTarget={() => { setStep("phone"); setCode(""); }}
              onResend={async () => {
                const { data } = await axios.post(`${config.API}/auth/otp/send`, { phone: indianMobile10(phone) });
                setHint(data.message || "OTP sent");
              }}
            />
          </form>
        )}

        {method === "password" && (
          <form onSubmit={passwordLogin} className="mt-6 space-y-4">
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Registered email"
              className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="staff-login-email" />
            <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
              className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid="staff-login-password" />
            <button disabled={loading} className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold" data-testid="staff-login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </button>
            <button type="button" className="w-full text-sm text-[#673c96]" onClick={() => setShowForgot(true)}>Forgot password?</button>
            <p className="text-xs text-[#6b7280]">Set or change your password from Profile inside the portal. No password yet? Use phone OTP or ask admin to generate one.</p>
          </form>
        )}

        {error && <p className="mt-4 text-sm text-[#ee4036]" data-testid="staff-auth-error">{error}</p>}
        {showForgot && <ForgotPassword onClose={() => setShowForgot(false)} />}
      </div>
    </div>
  );
}

/** Portal layout: brand header + sidebar nav (mirrors the customer account shell). */
export function StaffShell({ roleLabel, user, nav, active, onNavigate, onLogout, children, portalPath = "/", onProfileClick, notificationsPath }) {
  const name = (user?.full_name || "").trim() || "Team member";
  const initials = name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const site = useSiteContent();
  const logoSrc = brandLogo(site);
  return (
    <div className="min-h-screen bg-[#f4f5f7]" data-testid="staff-shell">
      <header className="fixed top-0 inset-x-0 z-40 bg-white border-b border-[#673c96]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[64px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to={portalPath}
              className="flex flex-col items-start justify-center shrink-0"
              style={{ minWidth: '170px' }}
            >
              <img
                src={logoSrc}
                alt="BookMyJab"
                style={{
                  width: '160px',
                  minWidth: '140px',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
              <span
                className="text-[9px] md:text-[10px] italic font-medium text-[#311b52]/70 tracking-[0.12em] uppercase leading-tight mt-0.5"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Uncompromising Care. Seamlessly Yours.
              </span>
            </Link>
            <span className="px-2.5 py-1 rounded-full bg-[#673c96]/10 text-[#673c96] text-[11px] font-semibold uppercase tracking-wider">
              {roleLabel}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {notificationsPath ? <StaffNotificationBell path={notificationsPath} /> : null}
            {onProfileClick ? (
              <button
                type="button"
                onClick={onProfileClick}
                className="flex items-center gap-2 group"
                data-testid="staff-profile-btn"
                title="Open my profile"
              >
                <span className="hidden sm:block text-sm font-medium text-[#311b52] group-hover:underline transition-all" style={{ fontFamily: "Montserrat" }}>{name}</span>
                <span className="w-9 h-9 rounded-full bg-[#673c96]/15 text-[#673c96] flex items-center justify-center text-xs font-semibold ring-2 ring-transparent group-hover:ring-[#673c96]/40 transition-all">
                  {initials || "BJ"}
                </span>
              </button>
            ) : (
              <>
                <span className="hidden sm:block text-sm font-medium text-[#311b52]" style={{ fontFamily: "Montserrat" }}>{name}</span>
                <span className="w-9 h-9 rounded-full bg-[#673c96]/15 text-[#673c96] flex items-center justify-center text-xs font-semibold">{initials || "BJ"}</span>
              </>
            )}
            <button type="button" onClick={onLogout}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#6b7280] hover:text-[#311b52] transition-colors"
              data-testid="staff-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="pt-[64px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <aside className="lg:w-56 shrink-0">
              <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-1 lg:pb-0" data-testid="staff-sidebar">
                {nav.map((item) => {
                  const Icon = item.icon;
                  const isActive = active === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => onNavigate(item.id)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors text-left ${
                        isActive ? "bg-[#673c96] text-white shadow-sm" : "text-[#4b5563] hover:bg-white hover:text-[#311b52]"
                      }`}
                      style={{ fontFamily: "Montserrat" }}
                      data-testid={`staff-nav-${item.id}`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? "text-white" : "text-[#673c96]"}`} />
                      {item.label}
                      {item.badge ? (
                        <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/20 text-white" : "bg-[#f37721]/10 text-[#f37721]"}`}>
                          {item.badge}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </nav>
            </aside>
            <main className="flex-1 min-w-0">{children}</main>
          </div>
        </div>
      </div>
    </div>
  );
}

function StaffNotificationBell({ path }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    axios.get(`${config.API}${path}`, { headers: staffHeaders() })
      .then(({ data }) => {
        setItems(data.notifications || []);
        setUnread(data.unread_count || 0);
      })
      .catch(() => {});
  }, [path]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const markRead = async () => {
    setOpen((v) => !v);
    if (!open && unread) {
      try {
        await axios.post(`${config.API}${path}/mark-read`, {}, { headers: staffHeaders() });
        setUnread(0);
      } catch { /* ignore */ }
    }
  };

  return (
    <div className="relative">
      <button type="button" onClick={markRead} className="relative p-2 rounded-full hover:bg-[#673c96]/10" data-testid="staff-notif-bell" aria-label="Notifications">
        <Bell className="w-5 h-5 text-[#673c96]" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#f37721] text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-xl shadow-lg border border-[#673c96]/15 z-50 p-2">
          {items.length === 0 ? (
            <p className="text-sm text-[#6b7280] p-3">No notifications yet.</p>
          ) : items.map((n) => (
            <div key={n.id} className="px-3 py-2 rounded-lg hover:bg-[#f5f3f7]">
              <p className="text-sm font-semibold text-[#311b52]">{n.title}</p>
              <p className="text-xs text-[#673c96] mt-0.5">{n.message || n.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function paymentSucceeded(status) {
  return status === "paid" || status === "paid_offline";
}

export const STATUS_CHIP = {
  awaiting_payment: "bg-amber-100 text-amber-800",
  pending_confirmation: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  assigned: "bg-indigo-100 text-indigo-800",
  on_the_way: "bg-cyan-100 text-cyan-800",
  arrived: "bg-purple-100 text-purple-800",
  ongoing: "bg-orange-100 text-orange-700",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-gray-200 text-gray-600",
};

export function StatusChip({ status }) {
  const label = (status || "").replaceAll("_", " ");
  return (
    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${STATUS_CHIP[status] || "bg-gray-100 text-gray-700"}`}>
      {label}
    </span>
  );
}

export function SectionCard({ title, subtitle, children, actions }) {
  return (
    <div className="bg-white rounded-2xl border border-[#673c96]/10 p-5 sm:p-6">
      {(title || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            {title && <h2 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>{title}</h2>}
            {subtitle && <p className="text-sm text-[#6b7280] mt-0.5" style={{ fontFamily: "Montserrat" }}>{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

export function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#673c96]/10 p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#673c96]/70">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent ? "text-[#f37721]" : "text-[#311b52]"}`} style={{ fontFamily: "Poppins" }}>{value}</p>
    </div>
  );
}
